import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserAttributes, fetchUserAttributes, confirmUserAttribute } from 'aws-amplify/auth';
import { User, Mail, Save, Edit3, AlertCircle, CheckCircle, Shield } from 'lucide-react';

const ProfileSection = () => {
  const { user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    username: user?.username || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Prepare attributes to update
      const attributesToUpdate: Record<string, string> = {};
      
      // Only update name if it's different from current user name
      if (formData.name !== user.name && formData.name.trim()) {
        attributesToUpdate.name = formData.name.trim();
      }
      
      // Check if email is different and valid
      const emailChanged = formData.email !== user.email && formData.email.trim();
      if (emailChanged) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          setError('Please enter a valid email address.');
          return;
        }
        attributesToUpdate.email = formData.email.trim();
      }
      
      // Update user attributes in Cognito
      if (Object.keys(attributesToUpdate).length > 0) {
        const result = await updateUserAttributes({
          userAttributes: attributesToUpdate
        });
        
        // Check if email verification is required (Cognito always requires verification for email changes)
        if (emailChanged) {
          setPendingEmailChange(formData.email.trim());
          setShowEmailVerification(true);
          setSuccess('A verification code has been sent to your new email address. Please check your email and enter the code below.');
        } else {
          // Fetch updated user attributes to get the latest data
          const updatedAttributes = await fetchUserAttributes();
          
          // Update the user context with new data
          const updatedUser = {
            ...user,
            name: updatedAttributes.name || updatedAttributes.given_name || user.name,
            email: updatedAttributes.email || user.email,
          };
          
          setUser(updatedUser);
          setSuccess('Profile updated successfully!');
          setIsEditing(false);
        }
      } else {
        setSuccess('No changes to save.');
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form data when user changes or when starting to edit
  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        username: user.username || '',
      });
    }
  }, [user]);

  // Clear messages after 5 seconds
  React.useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      username: user?.username || '',
    });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    setShowEmailVerification(false);
    setEmailVerificationCode('');
    setPendingEmailChange(null);
  };

  const handleEmailVerification = async () => {
    if (!emailVerificationCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: emailVerificationCode.trim()
      });

      // Fetch updated user attributes
      const updatedAttributes = await fetchUserAttributes();
      
      // Update the user context with new data
      const updatedUser = {
        ...user!,
        name: updatedAttributes.name || updatedAttributes.given_name || user!.name,
        email: updatedAttributes.email || user!.email,
      };
      
      setUser(updatedUser);
      setSuccess('Email address updated successfully!');
      setShowEmailVerification(false);
      setEmailVerificationCode('');
      setPendingEmailChange(null);
      setIsEditing(false);
    } catch (err) {
      console.error('Error verifying email:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify email. Please check the code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Profile Information</h2>
        <p className="text-neutral-600 dark:text-neutral-400">Update your personal information and preferences.</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-green-800 dark:text-green-200">{success}</span>
        </div>
      )}
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
              Full Name
            </label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-500 dark:disabled:text-neutral-400 transition-colors"
                placeholder="Enter your full name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              disabled={true}
              className="w-full px-4 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
              placeholder="Enter your username"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Username cannot be changed after account creation.
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing || showEmailVerification}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-500 dark:disabled:text-neutral-400 transition-colors"
                placeholder="Enter your email address"
              />
            </div>
            {!isEditing && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Click Edit to change your email address. You'll need to verify the new email.
              </p>
            )}
          </div>
        </div>

        {/* Email Verification Section */}
        {showEmailVerification && (
          <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">Verify Your New Email Address</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  We've sent a verification code to <strong>{pendingEmailChange}</strong>. Please enter the code below to complete the email change.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={emailVerificationCode}
                  onChange={(e) => setEmailVerificationCode(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 disabled:opacity-50 transition-colors"
                  placeholder="Enter the 6-digit code from your email"
                  maxLength={6}
                />
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleEmailVerification}
                  disabled={isLoading || !emailVerificationCode.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-tour="verify-email-button"
                >
                  <Shield size={16} />
                  {isLoading ? 'Verifying...' : 'Verify Email'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-4 py-2 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-6 border-t border-slate-300 dark:border-neutral-700">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-tour="save-profile-button"
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 border border-slate-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 border border-slate-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
              data-tour="edit-profile-button"
            >
              <Edit3 size={16} />
              Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSection; 